/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { GenericResponse } from '../models/generic-response';
import { WorkflowInstanceStatusCheckRequest } from '../models/workflow-instance-status-check-request';
import { WorkflowInstanceStatusCheckResponse } from '../models/workflow-instance-status-check-response';
import { WorkflowOutcomeCallbackRequest } from '../models/workflow-outcome-callback-request';

/**
 * Callbacks from external workflow systems (WMS)
 */
@Injectable({ providedIn: 'root' })
export class WorkflowCallbackService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `updateClinicalWorkflowOutcome()` */
  static readonly UpdateClinicalWorkflowOutcomePath =
    '/api/v1/workflow-callback';

  /**
   * Minimal callback used by WMS to push workflow outcomes back to clinical.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClinicalWorkflowOutcome()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalWorkflowOutcome$Response(
    params: {
      body: WorkflowOutcomeCallbackRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowCallbackService.UpdateClinicalWorkflowOutcomePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Minimal callback used by WMS to push workflow outcomes back to clinical.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClinicalWorkflowOutcome$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalWorkflowOutcome(
    params: {
      body: WorkflowOutcomeCallbackRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.updateClinicalWorkflowOutcome$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `checkWorkflowInstanceStatus()` */
  static readonly CheckWorkflowInstanceStatusPath =
    '/api/v1/workflow-callback/status-check';

  /**
   * Check status of multiple workflow instances.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `checkWorkflowInstanceStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  checkWorkflowInstanceStatus$Response(
    params: {
      body: WorkflowInstanceStatusCheckRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkflowInstanceStatusCheckResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowCallbackService.CheckWorkflowInstanceStatusPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkflowInstanceStatusCheckResponse>;
        })
      );
  }

  /**
   * Check status of multiple workflow instances.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `checkWorkflowInstanceStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  checkWorkflowInstanceStatus(
    params: {
      body: WorkflowInstanceStatusCheckRequest;
    },
    context?: HttpContext
  ): Observable<WorkflowInstanceStatusCheckResponse> {
    return this.checkWorkflowInstanceStatus$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<WorkflowInstanceStatusCheckResponse>
        ): WorkflowInstanceStatusCheckResponse => r.body
      )
    );
  }
}
